export const normalizeAuthToken = (rawToken) => {
  if (!rawToken) return '';
  return rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`;
};

export const normalizeStatus = (value) => (value || '').toString().trim();

export const formatDate = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(parsed);
};

export const formatDateTime = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
};

const localDateKey = (date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

// Compares calendar days rather than instants. Date-only values such as "2026-06-30"
// are used as-is, so a deadline never shifts a day because of the timezone offset.
export const isOnOrAfterToday = (value) => {
  if (!value) return false;
  const text = String(value).trim();
  const key = /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : null;
  if (key) return key >= localDateKey(new Date());

  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return false;
  return localDateKey(parsed) >= localDateKey(new Date());
};

export const uniqueCount = (values) =>
  new Set(
    values
      .map((v) => (v || '').toString().trim().toLowerCase())
      .filter(Boolean)
  ).size;

// Renders a value using the type declared by the server-side field schema.
export const formatFieldValue = (value, type) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  const text = String(value).trim();
  if (!text) return '';
  return type === 'date' ? formatDate(text) : text;
};

export const statusChipSx = (status) => {
  const s = normalizeStatus(status).toLowerCase();
  if (s === 'submitted') return { bgcolor: '#1b5e20', color: '#ffffff' };
  if (s === 'saved' || s.startsWith('saved')) return { bgcolor: '#4e342e', color: '#ffffff' };
  if (s === 'closed') return { bgcolor: '#263238', color: '#ffffff' };
  if (s === 'withdrawn') return { bgcolor: '#37474f', color: '#ffffff' };
  if (s === 'in review') return { bgcolor: '#0d47a1', color: '#ffffff' };
  if (s === 'accepted') return { bgcolor: '#2e7d32', color: '#ffffff' };
  if (s === 'rejected') return { bgcolor: '#b71c1c', color: '#ffffff' };
  if (s === 'sent') return { bgcolor: '#2e7d32', color: '#ffffff' };
  if (s === 'failed') return { bgcolor: '#b71c1c', color: '#ffffff' };
  return { bgcolor: '#e9e4dc', color: '#500000' };
};

export const EMAIL_TYPE_LABELS = {
  'proposal-status': 'Proposal decision',
  'student-digest': 'Student digest',
  'employer-digest': 'Employer digest',
};

export const OUTCOME_FILTERS = Object.freeze([
  'Accepted',
  'Rejected',
  'In Review',
  'Withdrawn',
  'Saved (not yet submitted)',
]);

// Flattens the project-grouped payload for searching and for the proposal view.
export const flattenProposals = (projects, orphans) => [
  ...projects.flatMap((project) =>
    (project.proposals || []).map((proposal) => ({
      ...proposal,
      projectTitle: project.requestTitle || '',
      requesterName: project.requesterName || '',
    }))
  ),
  ...(orphans || []).map((proposal) => ({
    ...proposal,
    projectTitle: proposal.requestTitle || '',
    requesterName: '',
    orphaned: true,
  })),
];

const haystack = (record, keys) =>
  keys
    .map((key) => record?.[key])
    .filter((v) => typeof v === 'string' || typeof v === 'number')
    .join(' ')
    .toLowerCase();

// Matches a project when the project itself matches, or when any of its
// proposals does, so searching a student's name still surfaces their project.
export const projectMatchesQuery = (project, query) => {
  if (!query) return true;
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  const projectFields = Object.values(project || {}).filter(
    (v) => typeof v === 'string' || typeof v === 'number'
  );
  if (projectFields.join(' ').toLowerCase().includes(needle)) return true;

  return (project.proposals || []).some((proposal) =>
    haystack(proposal, [
      'proposalTitle',
      'applicantName',
      'applicantEmail',
      'applicantUIN',
      'degreeProgram',
      'outcome',
      'proposalID',
    ]).includes(needle)
  );
};
